import { useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { TooltipContent, TooltipRoot, TooltipTrigger } from '../ui/tooltip';
import { fmtInt } from '../../utils/format';

// Hover card that breaks a bar into its parts (dashboard v2.2): a title, one row
// per part (colored dot, label, item count, no %), and a "Total" footer. Shared by
// the Data freshness bars, the Data sources pass and the Activity daily bars so
// all read the same. Needs a TooltipProvider above it.
//
// Placement (v2.3): never under the cursor (GAP px from the trigger), shown at
// once and hidden on leave. On touch, where Radix never opens a tooltip, a tap
// shows it and a tap elsewhere hides it. Radix flips it to the opposite side
// when the preferred one has no room.
// - "above" (Data freshness, Data sources): above the bar, horizontally on the
//   cursor but within the bar's edges. Radix positions content against its
//   trigger only, so the cursor's position becomes the content's alignOffset.
// - "beside" (Activity day columns): right of the column, top-aligned with it.
//   Radix flips it left, but never shifts a side card sideways, so when neither
//   side has room (a phone) it goes above the column instead, where Radix does
//   keep it on screen.

export const CARD_WIDTH = 280;
export const GAP = 8;
const EDGE = 16; // collisionPadding: the card's distance from the viewport edges

/** The card's width: CARD_WIDTH, or the viewport less its edges on a screen narrower than that. */
export function cardWidth(viewport: number): number {
  return Math.min(CARD_WIDTH, viewport - 2 * EDGE);
}

/** Whether a "beside" card fits on either side of its trigger. */
export function besideFits(trigger: { left: number; right: number }, viewport: number): boolean {
  const need = cardWidth(viewport) + GAP + EDGE;
  return viewport - trigger.right >= need || trigger.left >= need;
}

/** alignOffset (align="start") that centres the card on the cursor, kept within
 *  the trigger's left and right edges; a trigger narrower than the card gets it
 *  centred. */
export function cursorOffset(clientX: number, trigger: { left: number; width: number }, width = CARD_WIDTH): number {
  const room = trigger.width - width;
  if (room <= 0) return room / 2;
  return Math.min(Math.max(clientX - trigger.left - width / 2, 0), room);
}

export interface BreakdownRow {
  key: string;
  label: string;
  dot: CSSProperties;
  value: number;
}

export default function BreakdownTooltip({
  title,
  rows,
  total,
  placement = 'above',
  onOpenChange,
  children,
}: {
  title: string;
  rows: BreakdownRow[];
  total: string;
  placement?: 'above' | 'beside';
  onOpenChange?: (open: boolean) => void;
  children: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ above: placement === 'above', offset: 0, width: CARD_WIDTH });
  const trigger = useRef<HTMLButtonElement>(null);
  const change = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  // Placed on every move (before Radix opens the card), so it follows the cursor
  // and a resized or rotated screen gets the right side and width.
  const follow = (e: { clientX: number; currentTarget: Element }) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = cardWidth(window.innerWidth);
    const above = placement === 'above' || !besideFits(rect, window.innerWidth);
    const offset = above ? cursorOffset(e.clientX, rect, width) : 0;
    setPos(p => (p.above === above && p.offset === offset && p.width === width ? p : { above, offset, width }));
  };
  // preventDefault() on these skips Radix's own handler for the same event:
  // Radix hides a tooltip on press and click, and a lifted finger "leaves".
  // decision: hovercard — see decisions/hovercard.md
  return (
    <TooltipRoot open={open} onOpenChange={change} delayDuration={0} disableHoverableContent>
      <TooltipTrigger
        asChild
        ref={trigger}
        onPointerEnter={follow}
        onPointerMove={follow}
        onPointerDown={e => e.preventDefault()}
        onPointerLeave={e => { if (e.pointerType === 'touch') e.preventDefault(); }}
        onClick={e => {
          e.preventDefault();
          follow(e);
          change(true);
        }}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={pos.above ? 'top' : 'right'}
        align="start"
        sideOffset={GAP}
        alignOffset={pos.offset}
        collisionPadding={EDGE}
        // A tap on the trigger itself moves the card rather than closing it.
        onPointerDownOutside={e => { if (trigger.current?.contains(e.target as Node)) e.preventDefault(); }}
        data-testid="breakdown-card"
        data-placement={pos.above ? 'above' : 'beside'}
        data-offset={pos.offset}
        style={{ width: pos.width }}
        className="flex flex-col gap-2 rounded-xl border border-zinc-200 bg-white px-4 pb-3 pt-3.5 text-[13px] font-normal text-zinc-950 shadow-[0_10px_28px_rgba(9,9,11,0.10)] tabular-nums"
      >
        <span className="text-sm font-semibold">{title}</span>
        {rows.map(r => (
          <div key={r.key} data-testid="breakdown-row" className="flex items-center gap-2.5">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={r.dot} />
            <span className="text-zinc-600">{r.label}</span>
            <span className="ml-auto font-medium">{fmtInt(r.value)}</span>
          </div>
        ))}
        <div className="flex items-center border-t border-zinc-200 pt-2">
          <span className="font-medium">Total</span>
          <span className="ml-auto font-semibold">{total}</span>
        </div>
      </TooltipContent>
    </TooltipRoot>
  );
}
